import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import { BsFillMoonFill, BsFillSunFill, BsSun } from 'react-icons/bs';
import { FaSun } from 'react-icons/fa';
import { IoSunny } from 'react-icons/io5';
import { RiSunFill } from 'react-icons/ri';
import { useTheme } from '../../styles/ThemeProvider';
import { motion, AnimatePresence } from 'framer-motion';

const HeaderContainer = styled.header`
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 3rem);
  max-width: 1100px;
  z-index: 1000;
  background: ${props => props.$isScrolled 
    ? 'rgba(255, 255, 255, 0.92)' 
    : 'rgba(255, 255, 255, 0.75)'};
  backdrop-filter: blur(20px) saturate(1.4);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
  border: 1px solid ${props => props.$isScrolled 
    ? 'rgba(0, 0, 0, 0.08)' 
    : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 50px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  height: ${props => props.$isScrolled ? '56px' : '60px'};
  display: flex;
  align-items: center;
  box-shadow: ${props => props.$isScrolled 
    ? '0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)' 
    : '0 4px 20px rgba(0, 0, 0, 0.06)'};
  
  .dark-mode & {
    background: ${props => props.$isScrolled 
      ? 'rgba(10, 10, 16, 0.88)' 
      : 'rgba(10, 10, 16, 0.5)'};
    border-color: ${props => props.$isScrolled 
      ? 'rgba(255, 255, 255, 0.08)' 
      : 'rgba(255, 255, 255, 0.1)'};
    box-shadow: ${props => props.$isScrolled 
      ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.25)' 
      : '0 4px 20px rgba(0, 0, 0, 0.15)'};
  }

  @media (max-width: 768px) {
    top: 0.5rem;
    width: calc(100% - 1.2rem);
    border-radius: 25px;
    height: ${props => props.$isScrolled ? '48px' : '52px'};
  }

  @media (max-height: 500px) {
    top: 0.5rem;
    width: calc(100% - 1.2rem);
    border-radius: 25px;
    height: ${props => props.$isScrolled ? '46px' : '48px'};
  }
  
  @media (max-width: 480px) {
    top: 0.4rem;
    width: calc(100% - 1rem);
    border-radius: 22px;
    height: ${props => props.$isScrolled ? '46px' : '48px'};
  }
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  padding: 0 1.2rem 0 1.5rem;
  height: 100%;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 0.8rem 0 1.2rem;
    justify-content: space-between;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.6rem 0 1rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  font-family: var(--font-secondary);
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--dark);
  transition: all var(--transition-fast);
  position: relative;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
  
  .dark-mode & {
    color: var(--white);
  }

  &:hover {
    transform: translateY(-2px);
    color: var(--dark);
    
    .dark-mode & {
      color: var(--white);
    }
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  margin-right: 10px;
  transition: all var(--transition-fast);
  filter: brightness(0.2);
  
  @media (max-width: 768px) {
    height: 35px;
    margin-right: 8px;
  }
  
  @media (max-width: 480px) {
    height: 30px;
    margin-right: 6px;
  }
  
  .dark-mode & {
    filter: brightness(0) invert(1);
  }
  
  ${Logo}:hover & {
    transform: rotate(5deg);
  }
`;

const LogoHighlight = styled.span`
  color: var(--secondary);
  text-shadow: 0 0 1px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.15);
  
  .dark-mode & {
    text-shadow: none;
  }
  
  ${Logo}:hover & {
    color: var(--secondary);
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => (props.isOpen ? '0' : '-100%')};
    width: 80%;
    max-width: 300px;
    height: 100vh;
    height: 100dvh;
    background-color: #ffffff;
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 5rem;
    padding-bottom: 2rem;
    transition: right var(--transition-medium);
    box-shadow: -5px 0 30px rgba(0, 0, 0, 0.15);
    overflow-y: auto;
    z-index: 1000;
    border-radius: 0;

    .dark-mode & {
      background-color: #0e1420;
      box-shadow: -5px 0 30px rgba(0, 0, 0, 0.5);
    }
  }

  @media (max-height: 500px) {
    position: fixed;
    top: 0;
    right: ${props => (props.isOpen ? '0' : '-100%')};
    width: 80%;
    max-width: 300px;
    height: 100vh;
    height: 100dvh;
    background-color: #ffffff;
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 4rem;
    padding-bottom: 1rem;
    transition: right var(--transition-medium);
    box-shadow: -5px 0 30px rgba(0, 0, 0, 0.15);
    overflow-y: auto;
    z-index: 1000;
    border-radius: 0;

    .dark-mode & {
      background-color: #0e1420;
      box-shadow: -5px 0 30px rgba(0, 0, 0, 0.5);
    }
  }
`;

const NavLink = styled.a`
  margin: 0 0.8rem;
  color: var(--dark);
  font-weight: 500;
  font-size: 0.9rem;
  position: relative;
  transition: all var(--transition-fast);
  padding: 0.3rem 0;
  cursor: pointer;
  text-decoration: none;
  letter-spacing: 0.02em;

  .dark-mode & {
    color: rgba(255, 255, 255, 0.85);
  }

  &:hover {
    color: var(--secondary);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background-color: var(--secondary);
    transition: width var(--transition-fast);
    border-radius: 2px;
  }

  &:hover::after {
    width: 100%;
  }
  
  &.active {
    color: var(--primary);
    font-weight: 600;
    
    .dark-mode & {
      color: #ffffff;
    }
  }
  
  &.active::after {
    width: 100%;
    background-color: var(--secondary);
    
    .dark-mode & {
      background-color: var(--secondary);
    }
  }

  @media (max-width: 768px) {
    margin: 1.2rem 0;
    color: var(--dark);
    font-size: 1.1rem;

    &:hover {
      color: var(--secondary);
    }

    &::after {
      background-color: var(--secondary);
    }

    &.active {
      color: var(--primary);
      font-weight: 600;
    }

    .dark-mode & {
      color: #ffffff;

      &.active {
        color: var(--secondary);
      }
    }
  }

  @media (max-height: 500px) {
    margin: 1rem 0;
    color: var(--dark);
    font-size: 1rem;

    &:hover {
      color: var(--secondary);
    }

    &::after {
      background-color: var(--secondary);
    }

    &.active {
      color: var(--primary);
      font-weight: 600;
    }

    .dark-mode & {
      color: #ffffff;

      &.active {
        color: var(--secondary);
      }
    }
  }
`;

const DevisNavLink = styled(NavLink)`
  background-color: var(--secondary);
  color: var(--black) !important;
  padding: 0.4rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.85rem;
  margin-left: 0.5rem;
  
  &::after {
    display: none;
  }
  
  &:hover {
    background-color: var(--accent);
    color: var(--white) !important;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 1rem;
    padding: 0.7rem 2rem;
    font-size: 1rem;
    text-align: center;
    width: auto;
  }

  @media (max-height: 500px) {
    margin-left: 0;
    margin-top: 0.8rem;
    padding: 0.6rem 1.5rem;
    font-size: 0.95rem;
    text-align: center;
    width: auto;
  }
`;

const ThemeToggle = styled.button`
  background-color: rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: var(--dark);
  margin-left: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  width: 36px;
  height: 36px;
  border-radius: var(--border-radius-circle);
  position: relative;
  padding: 0;
  line-height: 1;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 2rem;
    width: 44px;
    height: 44px;
    background-color: rgba(0, 0, 0, 0.08);
    color: var(--dark);
    border-color: rgba(0, 0, 0, 0.15);

    .dark-mode & {
      background-color: rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.7);
      border-color: rgba(255, 255, 255, 0.1);
    }
  }

  @media (max-height: 500px) {
    margin-left: 0;
    margin-top: 1.5rem;
    width: 40px;
    height: 40px;
    background-color: rgba(0, 0, 0, 0.08);
    color: var(--dark);
    border-color: rgba(0, 0, 0, 0.15);

    .dark-mode & {
      background-color: rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.7);
      border-color: rgba(255, 255, 255, 0.1);
    }
  }
  
  .dark-mode & {
    background-color: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    color: var(--secondary);
    
    svg {
      color: var(--secondary);
    }
    
    &::before {
      transform: scale(1.5);
      opacity: 0.1;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, var(--secondary) 0%, transparent 70%);
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
    transition: transform 0.3s ease, opacity 0.3s ease;
    z-index: 0;
  }

  svg {
    font-size: 1.3rem;
    transition: color var(--transition-fast);
    position: relative;
    top: 1px;
    z-index: 1;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }
  
  .sun-icon {
    font-size: 1.5rem;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--dark);
  font-size: 1.8rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 0.5rem;
  z-index: 1001;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'none' : 'flex'};
    align-items: center;
    justify-content: center;
  }

  @media (max-height: 500px) {
    display: ${props => props.$isOpen ? 'none' : 'flex'};
    align-items: center;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    padding: 0.3rem;
  }
  
  &:hover {
    color: var(--secondary);
  }
  
  .dark-mode & {
    color: #ffffff;
    
    &:hover {
      color: var(--secondary);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--dark);
  font-size: 1.8rem;
  display: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-circle);
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
    transform: rotate(90deg);
  }

  .dark-mode & {
    color: var(--light);
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-height: 500px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  z-index: 998;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }

  @media (max-height: 500px) {
    display: block;
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  height: 3px;
  background-color: var(--white);
  bottom: -5px;
  border-radius: 3px;
  transition: all 0.3s ease;
  
  .dark-mode & {
    background-color: var(--white);
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { darkMode, toggleTheme } = useTheme();
  const [activeLink, setActiveLink] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Déterminer la section active en fonction du défilement
    const handleScroll = () => {
      // Gestion de la classe scrolled
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
      
      // Déterminer la section active
      const sections = ['accueil', 'services', 'realisations', 'a-propos', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveLink(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
  };
  
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      closeMenu();
    }
  };

  // Choisissez l'icône que vous préférez parmi ces options
  const SunIcon = FaSun; // Vous pouvez changer pour BsFillSunFill, BsSun, FaSun, IoSunny ou RiSunFill

  return (
    <HeaderContainer $isScrolled={isScrolled}>
      <NavContainer>
        <Logo to="/" as={Link}>
          GVW-<LogoHighlight>Tech</LogoHighlight>
        </Logo>

        <MenuButton onClick={toggleMenu} aria-label="Menu" $isOpen={isMenuOpen}>
          <FiMenu />
        </MenuButton>

        <AnimatePresence>
          {isMenuOpen && (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeMenu}
            />
          )}
        </AnimatePresence>

        <NavLinks isOpen={isMenuOpen}>
          <CloseButton onClick={closeMenu} aria-label="Fermer le menu">
            <FiX />
          </CloseButton>
          <NavLink 
            onClick={() => scrollToSection('accueil')}
            className={activeLink === 'accueil' ? 'active' : ''}
          >
            Accueil
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('services')}
            className={activeLink === 'services' ? 'active' : ''}
          >
            Services
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('realisations')}
            className={activeLink === 'realisations' ? 'active' : ''}
          >
            Réalisations
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('a-propos')}
            className={activeLink === 'a-propos' ? 'active' : ''}
          >
            À propos
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('contact')}
            className={activeLink === 'contact' ? 'active' : ''}
          >
            Contact
          </NavLink>
          <DevisNavLink 
            onClick={() => scrollToSection('devis')}
            className={activeLink === 'devis' ? 'active' : ''}
          >
            Devis gratuit
          </DevisNavLink>

          <ThemeToggle 
            onClick={toggleTheme}
            aria-label={darkMode ? "Passer en mode clair" : "Passer en mode sombre"}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={darkMode ? 'dark' : 'light'}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.3 }}
              >
                {darkMode ? <SunIcon className="sun-icon" /> : <BsFillMoonFill />}
              </motion.div>
            </AnimatePresence>
          </ThemeToggle>
        </NavLinks>
      </NavContainer>
    </HeaderContainer>
  );
};

export default Header;
